import React, { createContext, useContext, useRef } from "react";
import Wizard from "./components/Wizard";
import { useWizardProvider } from "./hooks/useWizardProvider";
import { IWizardProviderContext, IWizardConfig } from "./types";
import { mutateErrors } from "../../utils";
import Loading from "../../components/Loading";
import { useNotify } from "../notify/useNotify";
import { NotifyMode } from "../notify/NotifyModeEnum";

const WizardContext = createContext<IWizardProviderContext>(
  {} as IWizardProviderContext,
);

export const useWizard = () => useContext(WizardContext);

export const WizardProvider: React.FC = ({ children }) => {
  const wizardProps = useWizardProvider();
  const snackNotify = useNotify();

  const {
    data: { instances, isLoading },
    actions: { setIsLoading, setStepValidity, createInstance, deleteInstance },
  } = wizardProps;

  // todo useCallback
  const openWizard = async (options: IWizardConfig) => {
    return new Promise(async (resolveCallback) => {
      let initialData = {};

      if (typeof options.getInitialData === "function") {
        setIsLoading(true);
        try {
          initialData = await options.getInitialData();
          setIsLoading(false);
        } catch (e) {
          const { message } = mutateErrors(e);
          snackNotify("Error", message, NotifyMode.ERROR);
          console.error(e);
          resolveCallback(null);
          setIsLoading(false);
          return;
        }
      }

      createInstance({
        ...options,
        resolveCallback,
        initialData,
      });
    });
  };

  const closeWizard = (id: number) => {
    deleteInstance(id);
  };

  const contextValue = useRef({ openWizard, closeWizard } as const);

  return (
    <WizardContext.Provider value={contextValue.current}>
      {isLoading && <Loading />}
      {instances.map((instance) => (
        <Wizard
          key={instance.id}
          deleteInstance={deleteInstance}
          setStepValidity={setStepValidity}
          id={instance.id}
          submitButtonText={instance.submitButtonText}
          submitButtonColor={instance.submitButtonColor}
          cancelButtonText={instance.cancelButtonText}
          steps={instance.steps}
          saveCallback={instance.saveCallback}
          getButtonConfigs={instance.getButtonConfigs}
          resolveCallback={instance.resolveCallback}
          mode={instance.mode}
          initialData={instance.initialData}
          showSuccessNotification={instance.showSuccessNotification}
        />
      ))}
      {children}
    </WizardContext.Provider>
  );
};
